'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FormData {
  name: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  phone?: string;
  subject?: string;
  message?: string;
}

const ContactForm = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Nama diperlukan';
    }

    if (formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Format e-mel tidak sah';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Nombor telefon diperlukan';
    } else if (!/^(\+?6?01)[0-46-9]-*[0-9]{7,8}$/.test(formData.phone.replace(/\s/g, ''))) {
      newErrors.phone = 'Format nombor telefon Malaysia tidak sah';
    }

    if (!formData.subject.trim()) {
      newErrors.subject = 'Subjek diperlukan';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Mesej diperlukan';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Mesej mestilah sekurang-kurangnya 10 aksara';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    await new Promise((resolve) => setTimeout(resolve, 1500));

    setIsSubmitting(false);
    setSubmitSuccess(true);
    setFormData({
      name: '',
      email: '',
      phone: '',
      subject: '',
      message: '',
    });

    setTimeout(() => {
      setSubmitSuccess(false);
    }, 5000);
  };

  if (!isHydrated) {
    return (
      <div className="bg-card rounded-xl shadow-md p-6 md:p-8 border border-border">
        <div className="flex items-center space-x-3 mb-6">
          <div className="bg-gradient-to-br from-primary/10 to-secondary/10 rounded-full p-3">
            <Icon name="EnvelopeIcon" size={24} variant="outline" className="text-primary" />
          </div>
          <h3 className="text-xl font-heading font-bold text-textPrimary">Hantar Mesej</h3>
        </div>
        <div className="animate-pulse space-y-4">
          <div className="h-12 bg-muted rounded-lg" />
          <div className="h-12 bg-muted rounded-lg" />
          <div className="h-12 bg-muted rounded-lg" />
          <div className="h-12 bg-muted rounded-lg" />
          <div className="h-32 bg-muted rounded-lg" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-[2rem] shadow-soft p-8 md:p-12 border border-border">
      <div className="mb-6">
        <h3 className="text-2xl font-heading font-bold text-textPrimary">Hantar Mesej</h3>
      </div>

      {submitSuccess && (
        <div className="mb-6 bg-success/10 border border-success/20 rounded-lg p-4 flex items-start space-x-3">
          <Icon
            name="CheckCircleIcon"
            size={24}
            variant="solid"
            className="text-success flex-shrink-0"
          />
          <div>
            <p className="font-body font-semibold text-success mb-1">Mesej Berjaya Dihantar!</p>
            <p className="font-body text-sm text-textSecondary">
              Terima kasih atas mesej anda. Kami akan menghubungi anda dalam masa 24 jam.
            </p>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <label
            htmlFor="name"
            className="block text-sm font-body font-semibold text-textPrimary mb-2"
          >
            Nama Penuh <span className="text-error">*</span>
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className={`w-full px-5 py-4 rounded-[1rem] border ${
              errors.name ? 'border-error' : 'border-input'
            } bg-background text-textPrimary font-body focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all duration-300`}
            placeholder="Masukkan nama penuh anda"
          />
          {errors.name && (
            <p className="mt-1 text-sm text-error font-body flex items-center space-x-1">
              <Icon name="ExclamationCircleIcon" size={16} variant="solid" />
              <span>{errors.name}</span>
            </p>
          )}
        </div>

        <div>
          <label
            htmlFor="email"
            className="block text-sm font-body font-semibold text-textPrimary mb-2"
          >
            E-mel (Opsional)
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className={`w-full px-5 py-4 rounded-[1rem] border ${
              errors.email ? 'border-error' : 'border-input'
            } bg-background text-textPrimary font-body focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all duration-300 placeholder:text-textTertiary`}
            placeholder="nama@contoh.com"
          />
          {errors.email && (
            <p className="mt-1 text-sm text-error font-body flex items-center space-x-1">
              <Icon name="ExclamationCircleIcon" size={16} variant="solid" />
              <span>{errors.email}</span>
            </p>
          )}
        </div>

        <div>
          <label
            htmlFor="phone"
            className="block text-sm font-body font-semibold text-textPrimary mb-2"
          >
            Nombor Telefon <span className="text-error">*</span>
          </label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            className={`w-full px-5 py-4 rounded-[1rem] border ${
              errors.phone ? 'border-error' : 'border-input'
            } bg-background text-textPrimary font-body focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all duration-300`}
            placeholder="012-345 6789"
          />
          {errors.phone && (
            <p className="mt-1 text-sm text-error font-body flex items-center space-x-1">
              <Icon name="ExclamationCircleIcon" size={16} variant="solid" />
              <span>{errors.phone}</span>
            </p>
          )}
        </div>

        <div>
          <label
            htmlFor="subject"
            className="block text-sm font-body font-semibold text-textPrimary mb-2"
          >
            Minat Servis <span className="text-error">*</span>
          </label>
          <select
            id="subject"
            name="subject"
            value={formData.subject}
            onChange={handleChange}
            className={`w-full px-5 py-4 rounded-[1rem] border ${
              errors.subject ? 'border-error' : 'border-input'
            } bg-background text-textPrimary font-body focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all duration-300`}
          >
            <option value="">Pilih Servis</option>
            <option value="lipat-baju">Lipat Baju</option>
            <option value="gosok-baju">Gosok Baju</option>
            <option value="lain-lain">Lain-lain</option>
          </select>
          {errors.subject && (
            <p className="mt-1 text-sm text-error font-body flex items-center space-x-1">
              <Icon name="ExclamationCircleIcon" size={16} variant="solid" />
              <span>{errors.subject}</span>
            </p>
          )}
        </div>

        <div>
          <label
            htmlFor="message"
            className="block text-sm font-body font-semibold text-textPrimary mb-2"
          >
            Mesej <span className="text-error">*</span>
          </label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            rows={4}
            className={`w-full px-5 py-4 rounded-[1rem] border ${
              errors.message ? 'border-error' : 'border-input'
            } bg-background text-textPrimary font-body focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all duration-300 resize-none placeholder:text-textTertiary`}
            placeholder="Apa yang kami boleh bantu anda?"
          />
          {errors.message && (
            <p className="mt-1 text-sm text-error font-body flex items-center space-x-1">
              <Icon name="ExclamationCircleIcon" size={16} variant="solid" />
              <span>{errors.message}</span>
            </p>
          )}
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full flex items-center justify-center gap-2 px-8 py-5 bg-secondary text-secondary-foreground rounded-2xl font-cta font-bold hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? (
            <>
              <Icon name="ArrowPathIcon" size={20} variant="outline" className="animate-spin" />
              <span>Menghantar...</span>
            </>
          ) : (
            <>
              <Icon name="PaperAirplaneIcon" size={20} variant="outline" />
              <span>Hantar Mesej</span>
            </>
          )}
        </button>
      </form>
    </div>
  );
};

export default ContactForm;
